<div class="ble-wifi-devices-container">
  <!-- Top compact header -->
  <div class="compact-header mb-1">
    <div class="d-flex align-items-center gap-1 overflow-hidden">
      <span class="devices-title">Devices</span>
      @if (!isLoading) {
        <span class="badge count-badge">
          {{ total || records.length }}
        </span>
      }
      @if (incidentId !== null && incidentId !== undefined && incidentId !== '') {
        <span class="badge incident-mini-badge" [title]="'Active Incident #' + incidentId">
          <i class="fa-solid fa-triangle-exclamation"></i> #{{ incidentId }}
        </span>
      }
      @if (autoRefresh) {
        <span class="live-dot" title="Live Auto-polling (10s)"></span>
      }
    </div>

    <div class="d-flex align-items-center gap-1">
      <button
        type="button"
        class="btn-mini-icon"
        [class.active]="autoRefresh"
        (click)="toggleAutoRefresh()"
        [title]="autoRefresh ? 'Auto-polling: ON (10s)' : 'Auto-polling: OFF'">
        <i class="fa-solid fa-clock-rotate-left"></i>
      </button>

      <button
        type="button"
        class="btn-mini-icon refresh-btn"
        (click)="fetchDevices()"
        [disabled]="isLoading"
        title="Refresh devices list">
        <i class="fa-solid fa-rotate" [class.fa-spin]="isLoading"></i>
      </button>
    </div>
  </div>

  <!-- Search & Time Preset Row (side by side to save height) -->
  <div class="search-time-row mb-1">
    <div class="search-input-wrap">
      <i class="fa fa-search search-icon"></i>
      <input
        type="text"
        class="device-search-input"
        [(ngModel)]="searchQuery"
        placeholder="Filter by name, MAC, platform..." />
      @if (searchQuery) {
        <button type="button" class="clear-search-btn" (click)="searchQuery = ''" title="Clear search">
          <i class="fa fa-times"></i>
        </button>
      }
    </div>

    <select
      class="time-select"
      [ngModel]="datePreset"
      (ngModelChange)="setDatePreset($event)"
      title="Filter by time range">
      <option value="5min">Last 5 min</option>
      <option value="10min">Last 10 min</option>
      <option value="30min">Last 30 min</option>
      <option value="1hour">Last 1 hr</option>
      <option value="all">All Time</option>
    </select>
  </div>

  <!-- Filter Pills Bar (Combined Transport & OS in a single tight line) -->
  <div class="filter-pills-bar mb-2">
    <span class="pill-chip" [class.active]="transportFilter === 'ALL'" (click)="transportFilter = 'ALL'">
      All ({{ records.length }})
    </span>
    <span class="pill-chip chip-ble" [class.active]="transportFilter === 'BLE'" (click)="transportFilter = 'BLE'">
      <i class="fa-brands fa-bluetooth-b me-1"></i>BLE ({{ getTransportCount('BLE') }})
    </span>
    <span class="pill-chip chip-wifi" [class.active]="transportFilter === 'WiFi'" (click)="transportFilter = 'WiFi'">
      <i class="fa-solid fa-wifi me-1"></i>WiFi ({{ getTransportCount('WiFi') }})
    </span>

    <span class="pill-separator">|</span>

    <span class="pill-chip" [class.active]="platformFilter === 'ALL'" (click)="platformFilter = 'ALL'">
      All OS
    </span>
    <span class="pill-chip chip-ios" [class.active]="platformFilter === 'iOS'" (click)="platformFilter = 'iOS'" *ngIf="getPlatformCount('iOS') > 0">
      <i class="fa-brands fa-apple me-1"></i>iOS ({{ getPlatformCount('iOS') }})
    </span>
    <span class="pill-chip chip-android" [class.active]="platformFilter === 'Android'" (click)="platformFilter = 'Android'" *ngIf="getPlatformCount('Android') > 0">
      <i class="fa-brands fa-android me-1"></i>Android ({{ getPlatformCount('Android') }})
    </span>
  </div>

  <!-- Loading State -->
  @if (isLoading) {
    <div class="loading-state text-center py-3">
      <div class="spinner-border spinner-border-sm text-primary mb-1" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
      <div class="loading-text text-muted small">Loading devices...</div>
    </div>
  }

  <!-- Error State -->
  @if (!isLoading && isError) {
    <div class="error-card p-2 mb-2 text-center rounded">
      <div class="text-warning extra-small mb-1"><i class="fa-solid fa-triangle-exclamation me-1"></i>{{ errorMessage }}</div>
      <button type="button" class="btn btn-xs btn-outline-danger py-0" (click)="fetchDevices()">
        <i class="fa-solid fa-rotate-right me-1"></i>Retry
      </button>
    </div>
  }

  <!-- Empty State -->
  @if (!isLoading && !isError && getFilteredRecords().length === 0) {
    <div class="empty-card text-center py-3">
      <i class="fa-solid fa-satellite-dish text-muted fs-4 mb-1"></i>
      <div class="text-white small fw-semibold">No Devices Found</div>
      <div class="text-muted extra-small">No devices match current filters</div>
      @if (searchQuery || platformFilter !== 'ALL' || transportFilter !== 'ALL') {
        <button type="button" class="btn btn-xs btn-link text-info p-0 mt-1" (click)="searchQuery = ''; platformFilter = 'ALL'; transportFilter = 'ALL'">
          Clear filters
        </button>
      }
    </div>
  }

  <!-- Compact Devices Scroll List -->
  @if (!isLoading && !isError && getFilteredRecords().length > 0) {
    <div class="devices-scroll-list custom-scroll">
      @for (device of getFilteredRecords(); track (device.id || device.externalDeviceId || $index)) {
        <div class="device-card mb-1" [class.expanded]="expandedDeviceId === device.id">
          <!-- Card Header / Summary Row -->
          <div class="device-card-header d-flex align-items-center justify-content-between p-1 px-2" (click)="toggleExpand(device.id)">
            <!-- Left: Device Icon / Avatar -->
            <div class="d-flex align-items-center gap-2 overflow-hidden flex-grow-1">
              <div class="device-avatar" [class.avatar-ios]="(device.platform || '').toLowerCase().includes('ios')" [class.avatar-android]="(device.platform || '').toLowerCase().includes('android')">
                <i [class]="getPlatformIcon(device.platform)"></i>
              </div>

              <!-- Device Info Column -->
              <div class="device-info-col overflow-hidden flex-grow-1">
                <!-- Top Row: Name and Badges -->
                <div class="d-flex align-items-center gap-1 flex-wrap">
                  <span class="device-name fw-semibold text-white text-truncate" [title]="device.name || device.externalDeviceId || 'Unknown Device'">
                    {{ device.name || device.externalDeviceId || 'Unknown Device' }}
                  </span>

                  <!-- Transport Badge -->
                  <span class="badge transport-badge" [class.badge-ble]="(device.transport || '').toUpperCase() === 'BLE'" [class.badge-wifi]="(device.transport || '').toUpperCase() === 'WIFI'">
                    {{ (device.transport || '').toUpperCase() === 'BLE' ? 'BLE' : 'WiFi' }}
                  </span>

                  <!-- Platform Badge -->
                  @if (device.platform) {
                    <span class="badge platform-badge">
                      {{ device.platform }}
                    </span>
                  }

                  <!-- RSSI Signal Badge -->
                  @if (device.rssi !== null && device.rssi !== undefined) {
                    <span class="badge rssi-badge" [ngClass]="getRssiQuality(device.rssi).class" [title]="'RSSI: ' + device.rssi + ' dBm'">
                      <i class="fa-solid fa-signal"></i> {{ device.rssi }} dBm
                    </span>
                  }

                </div>

                <!-- Bottom Row: MAC, Manufacturer, Last Seen -->
                <div class="d-flex align-items-center gap-2 text-muted extra-small flex-wrap mt-0">
                  @if (device.advertisedMac || device.address) {
                    <span class="meta-item" [title]="'MAC: ' + (device.advertisedMac || device.address)">
                      <i class="fa-solid fa-network-wired me-1"></i>{{ device.advertisedMac || device.address }}
                    </span>
                  }

                  @if (device.manufacturerId) {
                    <span class="meta-item" [title]="'Manufacturer: ' + getManufacturerName(device.manufacturerId)">
                      <i class="fa-solid fa-microchip me-1"></i>{{ getManufacturerName(device.manufacturerId) }}
                    </span>
                  }

                  @if (device.lastSeen) {
                    <span class="meta-item" [title]="'Last seen: ' + formatDate(device.lastSeen)">
                      <i class="fa-regular fa-clock me-1"></i>{{ formatRelative(device.lastSeen) }}
                    </span>
                  }
                </div>
              </div>
            </div>

            <!-- Right: Locate & Chevron -->
            <div class="d-flex align-items-center">
              @if (device.coordinates && device.coordinates.length >= 2) {
                <button
                  type="button"
                  class="btn-mini-icon btn-locate me-1"
                  (click)="onSelectDevice(device, $event)"
                  title="Locate device on floor map">
                  <i class="fa-solid fa-crosshairs text-info"></i>
                </button>
              }
              <button
                type="button"
                class="btn-chevron"
                [title]="expandedDeviceId === device.id ? 'Collapse' : 'Expand'">
                <i class="fa-solid" [class.fa-chevron-down]="expandedDeviceId !== device.id" [class.fa-chevron-up]="expandedDeviceId === device.id"></i>
              </button>
            </div>
          </div>

          <!-- Expandable Details Section -->
          @if (expandedDeviceId === device.id) {
            <div class="device-expanded-details p-2">
              <div class="row g-1">
                @if (device.externalDeviceId) {
                  <div class="col-12 detail-entry">
                    <span class="detail-label">External Device ID:</span>
                    <span class="detail-value font-monospace">
                      {{ device.externalDeviceId }}
                      <button type="button" class="btn-copy ms-1" (click)="copyToClipboard(device.externalDeviceId, 'uuid'); $event.stopPropagation()" title="Copy UUID">
                        <i class="fa-solid fa-copy"></i>
                      </button>
                    </span>
                  </div>
                }

                @if (device.coordinates && device.coordinates.length >= 2) {
                  <div class="col-12 detail-entry">
                    <span class="detail-label"><i class="fa-solid fa-location-dot me-1 text-danger"></i>Coordinates:</span>
                    <span class="detail-value font-monospace text-info">
                      {{ device.coordinates[0] }}, {{ device.coordinates[1] }}
                      <button type="button" class="btn btn-xs btn-outline-info ms-2 py-0 px-1" (click)="onSelectDevice(device, $event)">
                        <i class="fa-solid fa-crosshairs me-1"></i>Focus
                      </button>
                    </span>
                  </div>
                }

                <div class="col-6 detail-entry">
                  <span class="detail-label">Record ID:</span>
                  <span class="detail-value">#{{ device.id }}</span>
                </div>
                <div class="col-6 detail-entry">
                  <span class="detail-label">Sensor ID:</span>
                  <span class="detail-value">{{ device.sensorId || 'N/A' }}</span>
                </div>

                <div class="col-6 detail-entry">
                  <span class="detail-label">Connectable:</span>
                  <span class="detail-value">{{ device.connectable ? 'Yes' : 'No' }}</span>
                </div>
                <div class="col-6 detail-entry">
                  <span class="detail-label">Tx Power:</span>
                  <span class="detail-value">{{ device.txPower !== null && device.txPower !== undefined ? device.txPower + ' dBm' : 'N/A' }}</span>
                </div>

                <div class="col-6 detail-entry">
                  <span class="detail-label">First Seen:</span>
                  <span class="detail-value">{{ formatDate(device.firstSeen) }}</span>
                </div>
                <div class="col-6 detail-entry">
                  <span class="detail-label">Last Seen:</span>
                  <span class="detail-value">{{ formatDate(device.lastSeen) }}</span>
                </div>

                @if (device.host || (device.ipAddresses && device.ipAddresses.length > 0)) {
                  <div class="col-12 detail-entry">
                    <span class="detail-label">Host & IPs:</span>
                    <span class="detail-value">
                      {{ device.host ? (device.host + ' - ') : '' }}
                      {{ (device.ipAddresses || []).join(', ') }}
                    </span>
                  </div>
                }

                @if (device.beaconUuid || device.major || device.minor) {
                  <div class="col-12 detail-entry">
                    <span class="detail-label">Beacon:</span>
                    <span class="detail-value font-monospace">
                      UUID: {{ device.beaconUuid || 'N/A' }} | Maj: {{ device.major }} | Min: {{ device.minor }}
                    </span>
                  </div>
                }

                @if (hasManufacturerData(device)) {
                  <div class="col-12 detail-entry">
                    <span class="detail-label">Manufacturer Data:</span>
                    <div class="raw-data-block font-monospace p-1 mt-1 rounded">
                      @for (key of getManufacturerDataKeys(device); track key) {
                        <div><strong class="text-info">{{ key }}:</strong> {{ device.manufacturerData?.[key] }}</div>
                      }
                    </div>
                  </div>
                }

                @if (device.rawData) {
                  <div class="col-12 detail-entry">
                    <span class="detail-label">Raw Data:</span>
                    <div class="raw-data-block font-monospace p-1 mt-1 rounded">
                      {{ device.rawData }}
                    </div>
                  </div>
                }
              </div>
            </div>
          }
        </div>
      }
    </div>

    <!-- Compact Footer -->
    <div class="compact-footer d-flex justify-content-between align-items-center pt-1 mt-1 border-top border-secondary">
      <span class="text-muted extra-small">
        Showing {{ getFilteredRecords().length }} of {{ total || records.length }} devices
      </span>

      <span class="extra-small text-muted">
        <i class="fa-solid fa-clock-rotate-left me-1"></i>Every 10s
      </span>
    </div>
  }
</div>
